import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@shared/services/auth.service';
import { Store } from '@ngxs/store';
import { SetUserPersonalData, SignIn } from '@store/auth/auth.actions';
import { RouteNames } from '../../../../route-names';
import { Credentials } from '../../../../types/auth';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '@shared/services/alert.service';
import { TuiButton, TuiIcon, TuiTextfield } from '@taiga-ui/core';
import { TuiButtonLoading, TuiPassword } from '@taiga-ui/kit';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-user-login-form',
  standalone: true,
  imports: [
    TuiTextfield,
    ReactiveFormsModule,
    TuiIcon,
    TuiPassword,
    TuiButtonLoading,
    TuiButton,
    AsyncPipe,
  ],
  templateUrl: './user-login-form.component.html',
  styleUrl: './user-login-form.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserLoginFormComponent implements OnInit {
  protected loading$ = new BehaviorSubject<boolean>(false);

  form = this.fb.group({
    login: ['', Validators.required],
    password: ['', Validators.required],
  });

  signIn$ = new Subject<void>();
  protected readonly RouteNames = RouteNames;
  constructor(
    private readonly router: Router,
    private readonly destroyRef: DestroyRef,
    private readonly alertService: AlertService,
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly auth: AuthService,
  ) {}

  ngOnInit() {
    this.signIn$
      .pipe(
        filter(() => !this.form.invalid),
        map(() => this.form.getRawValue()),
        tap(() => this.loading$.next(true)),
        switchMap(credentials =>
          this.auth.signIn(credentials as Credentials).pipe(
            tap(response => {
              this.store.dispatch(new SignIn(response));
            }),
            switchMap(() =>
              this.auth.getPersonalData().pipe(
                tap(data => {
                  this.store.dispatch(new SetUserPersonalData(data));
                }),
              ),
            ),
            tap(() => {
              this.router.navigate([RouteNames.HOME]);
              this.loading$.next(false);
            }),
            catchError(err => {
              this.alertService.showError(err.error);
              this.loading$.next(false);
              return of(null);
            }),
          ),
        ),
        catchError(() => {
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
